import React, { useState, useEffect } from 'react';
import { Tabs, message } from 'antd';
import { useDebts } from '../../hooks/useDebts';
import { useAuth } from '../../../auth/contexts/AuthContext';
import { savingsService } from '../../../savings/services/savingsService';
import { usePayments } from '../../hooks/usePayments';
import DebtStats from '../DebtStats';
import DebtForm from '../DebtForm';
import DebtList from '../DebtList';
import PaymentModal from '../PaymentModal';
import PaymentHistory from '../PaymentHistory';
import Charts from '../../../analytics/components/Charts';
import ImportExport from '../../../backup/components/ImportExport';
import SmartAnalytics from '../../../analytics/components/SmartAnalytics';
import SavingsManager from '../../../savings/components/SavingsManager';

import styles from './DebtTracker.module.css';

const { TabPane } = Tabs;

export default function DebtTracker() {
    const {
        debts,
        loading: debtsLoading,
        addDebt,
        updateDebt,
        deleteDebt,
        updatePaidAmount,
        refetch: refetchDebts
    } = useDebts();

    const {
        payments,
        loading: paymentsLoading,
        addPayment,
        refetch: refetchPayments
    } = usePayments();


    const { currentUser } = useAuth();

    const [editingDebt, setEditingDebt] = useState(null);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedDebt, setSelectedDebt] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [savings, setSavings] = useState([]);


    // Xử lý submit form nợ
    const handleDebtSubmit = async (debtData) => {
        setFormLoading(true);
        try {
            if (editingDebt) {
                await updateDebt(editingDebt.id, debtData);
                setEditingDebt(null);
            } else {
                await addDebt(debtData);
            }
        } catch (error) {
            console.error('Error submitting debt:', error);
        }
        setFormLoading(false);
    };

    useEffect(() => {
        if (currentUser) {
            fetchSavings();
        }
    }, [currentUser]);

    const fetchSavings = async () => {
        try {
            const savingsData = await savingsService.getSavings(currentUser.uid);
            setSavings(savingsData);
        } catch (error) {
            console.error('Error fetching savings:', error);
        }
    };


    // Xử lý chỉnh sửa nợ
    const handleEditDebt = (debt) => {
        setEditingDebt(debt);
    };

    // Hủy chỉnh sửa
    const handleCancelEdit = () => {
        setEditingDebt(null);
    };

    // Xử lý xóa nợ
    const handleDeleteDebt = async (debtId) => {
        try {
            await deleteDebt(debtId);
        } catch (error) {
            console.error('Error deleting debt:', error);
        }
    };

    // Mở modal thanh toán
    const handleOpenPayment = (debt) => {
        setSelectedDebt(debt);
        setPaymentModalVisible(true);
    };

    // Xử lý thanh toán
    const handlePaymentSubmit = async (paymentData) => {
        try {
            // Thêm payment record
            const paymentInfo = {
                debtId: selectedDebt.id,
                nguoiVay: selectedDebt.nguoiVay,
                soTienTra: paymentData.soTienTra,
                ngayTra: paymentData.ngayTra,
                ghiChu: paymentData.ghiChu || ''
            };

            await addPayment(paymentInfo);

            // Cập nhật số tiền đã trả trong debt
            const currentPaid = selectedDebt.soTienDaTra || 0;
            const newPaidAmount = currentPaid + paymentData.soTienTra;

            // Chỉ đánh dấu "đã trả" khi trả đủ 100%
            const isFullyPaid = newPaidAmount >= selectedDebt.soTien;

            await updatePaidAmount(selectedDebt.id, newPaidAmount, isFullyPaid);

            setPaymentModalVisible(false);
            setSelectedDebt(null);

            // Refresh data
            refetchDebts();
            refetchPayments();

            // Thông báo phù hợp
            if (isFullyPaid) {
                message.success('Đã trả hết nợ!');
            } else {
                const remaining = selectedDebt.soTien - newPaidAmount;
                message.success(`Thanh toán thành công! Còn lại: ${remaining.toLocaleString()} VNĐ`);
            }

        } catch (error) {
            console.error('Error processing payment:', error);
        }
    };

    // Đóng modal thanh toán
    const handleClosePayment = () => {
        setPaymentModalVisible(false);
        setSelectedDebt(null);
    };

    // Xử lý import dữ liệu
    const handleDataImported = () => {
        refetchDebts();
        refetchPayments();
    };

    const tabItems = [
        {

            key: '1',
            label: '📝 Quản lý nợ',
            children: (
                <div className={styles.tabContent}>
                    <DebtForm
                        onSubmit={handleDebtSubmit}
                        editingDebt={editingDebt}
                        onCancelEdit={handleCancelEdit}
                        loading={formLoading}
                    />

                    <DebtList
                        debts={debts}
                        loading={debtsLoading}
                        onEdit={handleEditDebt}
                        onDelete={handleDeleteDebt}
                        onPayment={handleOpenPayment}
                    />
                </div>
            )
        },
        {
            key: '2',
            label: '💳 Lịch sử thanh toán',
            children: (
                <div className={styles.tabContent}>
                    <PaymentHistory
                        payments={payments}
                        debts={debts}
                        loading={paymentsLoading}
                    />
                </div>
            )
        },
        {
            key: '3',
            label: '💰 Tiết kiệm',
            children: (
                <div className={styles.tabContent}>
                    <SavingsManager />
                </div>
            )
        },
        {
            key: '4',
            label: '📊 Báo cáo & Thống kê',
            children: (
                <div className={styles.tabContent}>
                    <SmartAnalytics debts={debts} payments={payments} />

                    <Charts debts={debts} payments={payments} />
                </div>
            )
        },
        {
            key: '5',
            label: '📁 Backup & Import/Export',
            children: (
                <div className={styles.tabContent}>
                    <ImportExport onDataImported={handleDataImported} />
                </div>
            )
        }
    ];

    return (
        <div className={styles.debtTracker}>
            <DebtStats debts={debts} savings={savings} />

            <Tabs
                defaultActiveKey="1"
                className={styles.mainTabs}
                items={tabItems}
            />

            <PaymentModal
                visible={paymentModalVisible}
                debt={selectedDebt}
                onSubmit={handlePaymentSubmit}
                onCancel={handleClosePayment}
                loading={paymentsLoading}
            />
        </div>
    );
}